// Copia local de los boletos de un evento (IndexedDB) y cola de escaneos pendientes de sincronizar.
// La llena el manifiesto (/checkin/manifest) y la consulta el escáner cuando no hay red.

const DB_VERSION = 1;

const request = (req) => new Promise((resolve, reject) => {
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const done = (tx) => new Promise((resolve, reject) => {
  tx.oncomplete = () => resolve();
  tx.onerror = () => reject(tx.error);
  tx.onabort = () => reject(tx.error);
});

const normalize = (text) => (text || '').toString().trim().toUpperCase();

export class OfflineStore {
  constructor(eventId) {
    this.name = `talon-checkin-${eventId}`;
    this.db = null;
  }

  static supported() {
    return typeof indexedDB !== 'undefined';
  }

  async open() {
    if (this.db) return this.db;

    const req = indexedDB.open(this.name, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      const tickets = db.createObjectStore('tickets', { keyPath: 'uid' });
      tickets.createIndex('code', 'code', { unique: false });
      tickets.createIndex('oldUids', 'oldUids', { multiEntry: true });
      db.createObjectStore('queue', { keyPath: 'id', autoIncrement: true });
      db.createObjectStore('meta', { keyPath: 'key' });
    };
    this.db = await request(req);
    return this.db;
  }

  tx(stores, mode = 'readonly') {
    return this.db.transaction(stores, mode);
  }

  // rows: [[uid, code, name, priceId, status, checkedInAt, invalidated, oldUids]]
  async upsertTickets(rows) {
    await this.open();
    const tx = this.tx('tickets', 'readwrite');
    const store = tx.objectStore('tickets');
    rows.forEach(([uid, code, name, priceId, status, checkedInAt, invalidated, oldUids]) => {
      store.put({
        uid: normalize(uid),
        code: code ? normalize(code) : null,
        name: name || '',
        priceId,
        status,
        checkedInAt: checkedInAt || null,
        invalidated: Boolean(invalidated),
        oldUids: (oldUids || []).map(normalize),
        pendingSync: false,
      });
    });
    await done(tx);
  }

  async count() {
    await this.open();
    return request(this.tx('tickets').objectStore('tickets').count());
  }

  // Devuelve { ticket, via } con via = 'uid' | 'code' | 'old' | null
  async resolve(raw) {
    await this.open();
    const text = normalize(raw);
    if (!text) return { ticket: null, via: null };

    const store = this.tx('tickets').objectStore('tickets');
    const byUid = await request(store.get(text));
    if (byUid) return { ticket: byUid, via: 'uid' };

    const byCode = await request(store.index('code').get(text));
    if (byCode) return { ticket: byCode, via: 'code' };

    const byOld = await request(store.index('oldUids').get(text));
    if (byOld) return { ticket: byOld, via: 'old' };

    return { ticket: null, via: null };
  }

  async markUsed(uid, { at, gate, device }) {
    await this.open();
    const tx = this.tx('tickets', 'readwrite');
    const store = tx.objectStore('tickets');
    const ticket = await request(store.get(normalize(uid)));
    if (ticket) {
      ticket.checkedInAt = at;
      ticket.gate = gate;
      ticket.device = device;
      ticket.pendingSync = true;
      store.put(ticket);
    }
    await done(tx);
  }

  async enqueue(item) {
    await this.open();
    const tx = this.tx('queue', 'readwrite');
    tx.objectStore('queue').add({ ...item, queuedAt: new Date().toISOString() });
    await done(tx);
  }

  async queued(limit = 50) {
    await this.open();
    const all = await request(this.tx('queue').objectStore('queue').getAll());
    return all.slice(0, limit);
  }

  async queueSize() {
    await this.open();
    return request(this.tx('queue').objectStore('queue').count());
  }

  async dequeue(ids) {
    await this.open();
    const tx = this.tx('queue', 'readwrite');
    ids.forEach((id) => tx.objectStore('queue').delete(id));
    await done(tx);
  }

  async getMeta(key) {
    await this.open();
    const row = await request(this.tx('meta').objectStore('meta').get(key));
    return row ? row.value : null;
  }

  async setMeta(key, value) {
    await this.open();
    const tx = this.tx('meta', 'readwrite');
    tx.objectStore('meta').put({ key, value });
    await done(tx);
  }

  // Búsqueda sencilla por nombre o código (recorre todo; 6,500 filas tardan milisegundos).
  async search(query, limit = 20) {
    await this.open();
    const q = normalize(query);
    if (q.length < 2) return [];

    const all = await request(this.tx('tickets').objectStore('tickets').getAll());
    return all
      .filter((t) => t.uid.includes(q) || (t.code && t.code.includes(q)) || normalize(t.name).includes(q))
      .slice(0, limit);
  }
}
